import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    forwardRef,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild
} from '@angular/core';
import {
    ControlValueAccessor,
    FormControl,
    FormGroup,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule
} from '@angular/forms';
import { noop, Subject } from 'rxjs';
import { NgForOf, NgIf } from '@angular/common';
import { takeUntil, tap } from 'rxjs/operators';
import { IconsModule } from '../../icons/icons.module';

@Component({
    selector: 'sb-slide-toggle',
    standalone: true,
    imports: [ReactiveFormsModule, NgForOf, IconsModule, NgIf],
    templateUrl: './sb-slide-toggle.component.html',
    styleUrls: ['./sb-slide-toggle.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SbSlideToggleComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SbSlideToggleComponent implements ControlValueAccessor, OnInit, OnDestroy {
    /**
     * Is toggle settable? If true, the active position is set even if form control is written to outside of the component
     * E.g., the toggle can be set by a parent component
     */
    @Input() settable = false;

    get disabled(): boolean {
        return this._disabled;
    }

    @Input() set disabled(value: boolean) {
        this._disabled = value;
        this.setDisabledState(this.disabled);
    }
    get options(): Array<any> {
        return this._options;
    }

    @Input() set options(value: Array<any>) {
        if (value?.length > 2 || Object.values(value || {}).some((v) => !!v.label)) {
            this.booleanMode = false;
        }
        this._options = value;
    }
    @HostBinding('style.--active-position') public activePosition: string;
    @HostBinding('style.--active-width') public activeWidth: string;
    @ViewChild('formElement') set formElement(element: ElementRef | undefined) {
        const nativeElement = element.nativeElement as HTMLElement;
        const activeElement = nativeElement.querySelector(
            '.form-check-input.active'
        ) as HTMLInputElement;
        if (activeElement) {
            const offset = (activeElement?.offsetParent as HTMLElement)?.offsetLeft || 0;
            this.activePosition = `${offset}px`;
            this.activeWidth = `${activeElement?.offsetWidth - 1 || 0}px`;
        }
    }

    private _disabled = false;
    id = `sb-slide-toggle-${Math.random().toString(36).substring(2, 15)}-`;

    private _options: Array<any> = [{ value: false }, { value: true }];

    private updateToggleUIFromValue(): void {
        const nativeElement = this._elRef.nativeElement as HTMLElement;
        const inputElements = Array.from(
            nativeElement.querySelectorAll('.form-check-input')
        ) as HTMLInputElement[];

        const activeElement = inputElements.find((input) =>
            input.classList.contains('active')
        );
        if (activeElement) {
            const offset = (activeElement.offsetParent as HTMLElement)?.offsetLeft ?? 0;
            this.activePosition = `${offset}px`;
            this.activeWidth = `${activeElement.offsetWidth - 1}px`;
            this._cdr.markForCheck();
        }
    }

    readonly form: FormGroup = new FormGroup<{ value: FormControl<any> }>({
        value: new FormControl<any>({ value: '', disabled: this._disabled })
    });

    unsubscribe$ = new Subject<boolean>();
    booleanMode = true;
    onChange: (value: unknown) => void = noop;
    onTouch: () => void = noop;

    registerOnChange(fn: (value: unknown) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouch = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        isDisabled ? this.form.get('value')?.disable() : this.form.get('value')?.enable();
    }

    writeValue(value: { value: string; label: string }): void {
        this.form.patchValue({ value }, { emitEvent: false });

        // If toggle is settable, adjust the active position as if clicked
        if (this.settable) {
            const target = document.querySelector(`input#${value.value}`);
            if (target) {
                this.adjustActivePosition(target as HTMLInputElement);
            }
        }
        this._cdr.detectChanges();
    }
    onChangeEvent(event: Event): void {
        // On click event
        const inputElement = event.target as HTMLInputElement;
        this.adjustActivePosition(inputElement);
    }

    adjustActivePosition(inputElement: HTMLInputElement): void {
        const offset = (inputElement.offsetParent as HTMLElement).offsetLeft;
        this.activePosition = `${offset}px`;
        this.activeWidth = `${inputElement.offsetWidth - 1}px`;
    }

    constructor(
        private _elRef: ElementRef,
        private _cdr: ChangeDetectorRef,
        private _renderer: Renderer2
    ) {}

    ngOnInit(): void {
        this.form.valueChanges
            .pipe(
                tap(({ value }) => {
                    this.onChange(value);
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }
}
